import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { DefaultLayoutComponent } from './containers';
import { InspectionFilesPickerComponent } from './inspection/inspection-files-picker/inspection-files-picker.component';
import { InspectionFilesComponent } from './inspection/inspection-files/inspection-files.component';
import { InspectionGalleryPopupComponent } from './inspection/inspection-gallery-popup/inspection-gallery-popup.component';
import { InspectionPopUpComponent } from './inspection/inspection-popup/inspection-popup.component';
import { InspectionResolver } from './inspection/inspector.resolver';
import { HazardHubReportPageComponent } from './spatial-analysis/hazard-hub-report/hazard-hub-report-page.component';
import { DownloadComponent } from './views/download/download.component';
import { P404Component } from './views/error/404.component';
import { P500Component } from './views/error/500.component';
import { ForgotComponent } from './views/forgot/forgot.component';
import { LoginComponent } from './views/login/login.component';
import { RecoverComponent } from './views/recover/recover.component';
import { RegisterComponent } from './views/register/register.component';
import { WelcomeComponent } from './views/welcome/welcome.component';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'inspection',
    pathMatch: 'full',
  },
  {
    path: '404',
    component: P404Component,
    data: {
      title: 'Page 404',
    },
  },
  {
    path: 'inspection/popup',
    component: InspectionPopUpComponent,
    data: { title: 'Popup' },
  },
  {
    path: 'inspection/grid-popup',
    component: InspectionGalleryPopupComponent,
    data: { title: 'Grid Popup' },
  },
  {
    path: 'hazardhub/report/:reportId',
    component: HazardHubReportPageComponent,
    data: { title: 'HazardHub Report' },
  },
  {
    path: '500',
    component: P500Component,
    data: {
      title: 'Page 500',
    },
  },
  {
    path: 'login',
    component: LoginComponent,
    data: {
      title: 'Login Page',
    },
  },
  {
    path: 'recommendation-letter',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./recommendation.letter/recommendation.letter.module').then(m => m.RecommendationLetterModule),
      },
    ],
  },
  {
    path: 'gallery',
    children: [
      {
        path: '',
        loadChildren: () => import('./external.gallery/external.gallery.module').then(m => m.ExternalGalleryModule),
      },
    ],
  },
  {
    // External Habgen GIS — keep under /gis/locations/* so bare /gis stays in the admin layout
    // (GisModule → One Map). A top-level empty `gis` child previously matched /gis with no
    // component and left a blank screen when leaving One Map via the sidebar.
    path: 'gis',
    children: [
      {
        path: 'locations',
        loadChildren: () => import('./external.gis/external.gis.module').then(m => m.ExternalGisModule),
      },
    ],
  },
  {
    path: 'forgot',
    component: ForgotComponent,
    data: {
      title: 'Forgot Password Page',
    },
  },
  {
    path: 'recover/:token',
    component: RecoverComponent,
    data: {
      title: 'Recover Page',
    },
  },
  {
    path: 'register',
    component: RegisterComponent,
    data: {
      title: 'Register Page',
    },
  },
  {
    path: 'download',
    component: DownloadComponent,
    data: {
      title: 'Download app',
    },
  },
  {
    path: 'download/:msg',
    component: DownloadComponent,
    data: {
      title: 'Download app',
    },
  },
  {
    path: 'inspection/box/viewer/:id',
    component: InspectionFilesComponent,
    resolve: { inspection: InspectionResolver },
    data: { title: 'Inspection files' },
  },
  {
    path: 'inspection/box/picker/:id',
    component: InspectionFilesPickerComponent,
    resolve: { inspection: InspectionResolver },
    data: { title: 'Inspection files' },
  },
  {
    path: '',
    component: DefaultLayoutComponent,
    data: {
      title: 'Home',
    },
    children: [
      {
        path: 'welcome',
        pathMatch: 'full',
        component: WelcomeComponent,
      },
      {
        path: 'otp',
        loadChildren: () => import('./otp/otp.module').then(m => m.OtpModule),
      },
      {
        path: 'customer',
        loadChildren: () => import('./customer/customer.module').then(m => m.CustomerModule),
      },
      {
        path: 'broker',
        loadChildren: () => import('./broker/broker.module').then(m => m.BrokerModule),
      },
      {
        path: 'organization',
        loadChildren: () => import('./organization/organization.module').then(m => m.OrganizationModule),
      },
      {
        path: 'onboarding',
        loadChildren: () => import('./onboarding/onboarding.module').then(m => m.OnboardingModule),
      },
      {
        path: 'version',
        loadChildren: () => import('./version/version.module').then(m => m.VersionModule),
      },
      {
        path: 'policy',
        loadChildren: () => import('./policy/policy.module').then(m => m.PolicyModule),
      },
      {
        path: 'policyGroup',
        loadChildren: () => import('./policy.group/policy.group.module').then(m => m.PolicyGroupModule),
      },
      {
        path: 'gis',
        loadChildren: () => import('./gis/gis.module').then(m => m.GisModule),
      },
      {
        path: 'role',
        loadChildren: () => import('./role/role.module').then(m => m.RoleModule),
      },
      {
        path: 'help',
        loadChildren: () => import('./help/help.module').then(m => m.HelpModule),
      },
      {
        path: 'helpCategory',
        loadChildren: () => import('./help.category/help.category.module').then(m => m.HelpCategoryModule),
      },
      {
        path: 'helpSection',
        loadChildren: () => import('./help.section/help.section.module').then(m => m.HelpSectionModule),
      },
      {
        path: 'inspection',
        loadChildren: () => import('./inspection/inspection.module').then(m => m.InspectionModule),
      },
      {
        path: 'sync',
        loadChildren: () => import('./sync/sync.module').then(m => m.SyncModule),
      },
      {
        path: 'inspection/emailEvent',
        loadChildren: () => import('./sendgrid.event/sendgrid.event.module').then(m => m.SendgridEventModule),
      },
      {
        path: 'vendor',
        loadChildren: () => import('./vendor/vendor.module').then(m => m.VendorModule),
      },
      {
        path: 'flagGroupTag',
        loadChildren: () => import('./flag.group.tag/flag.group.tag.module').then(m => m.FlagGroupTagModule),
      },
      {
        path: 'inspector',
        loadChildren: () => import('./inspector/inspector.module').then(m => m.InspectorModule),
      },
      {
        path: 'question',
        loadChildren: () => import('./question/question.module').then(m => m.QuestionModule),
      },
      {
        path: 'section',
        loadChildren: () => import('./section/section.module').then(m => m.SectionModule),
      },
      {
        path: 'item',
        loadChildren: () => import('./item/item.module').then(m => m.ItemModule),
      },
      {
        path: 'inspection-builder-changelog',
        loadChildren: () => import('./change-log/change-log.module').then(m => m.ChangeLogModule),
      },
      {
        path: 'user',
        loadChildren: () => import('./user/user.module').then(m => m.UserModule),
      },
      {
        path: 'setting',
        loadChildren: () => import('./setting/setting.module').then(m => m.SettingModule),
      },
      {
        path: 'product-type',
        loadChildren: () => import('./product.type/product-type.module').then(m => m.ProductTypeModule),
      },
      {
        path: 'letterBlock',
        loadChildren: () => import('./letter.block/letter.block.module').then(m => m.LetterBlockModule),
      },
    ],
  },
  { path: '**', component: P404Component },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'disabled', // Prevent scrolling on navigation
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
